import React, { useState } from 'react';

import { MerchantsShowCredentialsForm } from 'pages/Merchants/MerchantsShow/MerchantsShowCredentials/components/MerchantsShowCredentialsForm';
import { MerchantsShowCredentialsFormSkeleton } from 'pages/Merchants/MerchantsShow/MerchantsShowCredentials/components/MerchantsShowCredentialsFormSkeleton';
import { useMerchantCredentialsJson } from 'pages/Merchants/MerchantsShow/MerchantsShowCredentials/hooks/useMerchantCredentialsJson';
import { Form, RecordContextProvider, useNotify, useUpdate } from 'react-admin';
import { FieldValues } from 'react-hook-form';
import { Alert } from 'shared/mui/Alert/Alert';
import { Button, DeleteButton, SaveButton } from 'shared/mui/Buttons';
import { CardActionsButtons, CardHeader } from 'shared/mui/Card';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { Dialog } from 'shared/mui/Dialogs';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { MerchantCredentials } from 'types/embedded/merchant';

type Props = {
  open: boolean;
  onClose: () => void;
  onDelete: (record: MerchantCredentials) => void;
  merchantId: string | undefined;
  merchantName: string | undefined;
  record: MerchantCredentials | undefined;
};

interface EditFormValues extends FieldValues {
  merchant: string;
  provider: string;
  name: string;
  comment: string;
  credentials: Record<string, any>;
}

export const MerchantsShowCredentialsEditDialog: React.FC<Props> = ({
  open,
  onClose,
  onDelete,
  merchantId,
  merchantName,
  record,
}) => {
  const [update] = useUpdate();
  const notify = useNotify();
  const [errors, setErrors] = useState<string[]>([]);
  const { credentials, isLoading, isError } = useMerchantCredentialsJson(record?.originId);

  const onSubmit = (formValues: EditFormValues) => {
    update(
      'merchant_credentials',
      {
        id: record?.id,
        data: {
          ...formValues,
          merchant: merchantId,
        },
      },
      {
        onSuccess: () => {
          notify('Реквизит доступа успешно отредактирован!', {
            type: 'success',
          });
          onClose();
        },
        onError: (error) => {
          setErrors((error as Error)?.message.split('\n'));
          notify(`Ошибка: ${(error as Error).message}`, { type: 'error' });
        },
      },
    );
  };

  return (
    <Dialog
      onClose={onClose}
      open={open}
      scroll="body"
      sx={{ '& .MuiPaper-root': { maxWidth: '1280px' } }}
    >
      <RecordContextProvider
        value={{
          name: record?.name,
          provider: record?.provider,
          comment: record?.comment,
          credentials,
        }}
      >
        <Form onSubmit={onSubmit as any} resource="merchant_credentials">
          <CardHeader
            appendActions={[<CloseIconButton key="close-button" onClose={onClose} />]}
            sx={{ padding: '24px' }}
            title="Редактирование реквизитов доступа"
            titleSize="smallBold"
          />
          <CardContent sx={{ padding: '0 24px !important' }}>
            {isLoading ? (
              <MerchantsShowCredentialsFormSkeleton />
            ) : isError ? (
              <Alert severity="error">Не удалось загрузить реквизиты доступа.</Alert>
            ) : (
              <MerchantsShowCredentialsForm errors={errors} merchantName={merchantName} />
            )}
          </CardContent>
          <CardActionsButtons
            leftActionsSlot={
              <DeleteButton
                onClick={() => {
                  onClose();
                  record && onDelete(record);
                }}
                variant="outlined"
              />
            }
            rightActionsSlot={[
              <Button key="cancel" label="Отменить" onClick={onClose} variant="outlined" />,
              <SaveButton disabled={isLoading || isError} key="create" label="Сохранить" />,
            ]}
          />
        </Form>
      </RecordContextProvider>
    </Dialog>
  );
};
